import { Link } from 'react-router-dom';
import { Instagram, Mail, MapPin, Clock } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

// WhatsApp Icon Component
const WhatsAppIcon = ({ size = 18, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  const { getContent } = useSiteContent();
  
  const whatsappNumber = getContent('whatsapp_number', '919793498889');
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=Hi,%20I'd%20like%20to%20book%20a%20tattoo%20consultation.`;
  const instagramUrl = getContent('instagram_url', 'https://www.instagram.com/vision.ttt');
  const studioEmail = getContent('studio_email', 'vision.tttlab@gmail.com');
  const studioAddress = getContent('studio_address', 'Satya Tower, Vibhuti Khand, Gomti Nagar, Lucknow-226010');
  const studioHours = getContent('studio_hours', '12 PM - 7 PM | By Appointment');
  const footerTagline = getContent('footer_tagline', 'Trust The Process');
  const footerDescription = getContent('footer_description', 'A private, appointment-only tattoo studio creating meaningful art through trust and connection.');
  const copyrightText = getContent('copyright_text', '© 2026 Vision Tattoo. All rights reserved.');

  return (
    <footer 
      data-testid="main-footer"
      className="bg-primary text-primary-foreground px-4 sm:px-6 md:px-12 lg:px-24 py-10 md:py-16"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
        {/* Brand Section */}
        <div className="sm:col-span-2 lg:col-span-1">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 mb-4">
            <div className="bg-white/90 rounded-full p-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_c2d28a08-ae0d-49cc-b72b-7be69e3b538c/artifacts/daj5mwbp_IMG_3269.png"
                alt="Vision Tattoo Logo"
                className="h-24 sm:h-28 w-auto object-contain mix-blend-multiply"
              />
            </div>
            <div>
              <h3 className="font-heading text-xl sm:text-2xl font-medium">Vision Tattoo</h3>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-70">
                {footerTagline}
              </p>
            </div>
          </Link>
          <p className="font-body text-sm leading-relaxed opacity-80 mt-3">
            {footerDescription}
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-heading text-sm font-semibold uppercase tracking-wider mb-4">Explore</h4>
          <div className="flex flex-col gap-2">
            <Link to="/quote-estimator" className="text-sm hover:opacity-70 transition-opacity">
              Book a Session
            </Link>
            <Link to="/gallery" className="text-sm hover:opacity-70 transition-opacity">
              F&T Gallery
            </Link>
            <Link to="/first-timer" className="text-sm hover:opacity-70 transition-opacity">
              First Timer Zone
            </Link>
            <Link to="/healing-tracker" className="text-sm hover:opacity-70 transition-opacity">
              Healing Tracker
            </Link>
            <Link to="/blog" className="text-sm hover:opacity-70 transition-opacity">
              Blog
            </Link>
          </div>
        </div>

        {/* Services */}
        <div>
          <h4 className="font-heading text-sm font-semibold uppercase tracking-wider mb-4">Services</h4>
          <div className="flex flex-col gap-2">
            <Link to="/gift-cards" className="text-sm hover:opacity-70 transition-opacity">
              Gift Cards
            </Link>
            <Link to="/collaborate" className="text-sm hover:opacity-70 transition-opacity">
              Collaborate With Us
            </Link>
            <Link to="/contact" className="text-sm hover:opacity-70 transition-opacity">
              Contact
            </Link>
          </div>
        </div>

        {/* Contact & Social */}
        <div>
          <h4 className="font-heading text-sm font-semibold uppercase tracking-wider mb-4">Connect</h4>
          
          {/* Social Icons */}
          <div className="flex gap-3 mb-5">
            <a 
              href={instagramUrl}
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a 
              href={whatsappLink}
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-link"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              aria-label="WhatsApp"
            >
              <WhatsAppIcon size={18} />
            </a>
            <a 
              href={`mailto:${studioEmail}`}
              data-testid="footer-email-link"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              aria-label="Email"
            >
              <Mail size={18} />
            </a>
          </div>

          {/* Studio Info */}
          <div className="space-y-3 text-sm opacity-80">
            <div className="flex items-start gap-2">
              <MapPin size={16} className="mt-0.5 flex-shrink-0" />
              <p>{studioAddress}</p>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} className="flex-shrink-0" />
              <p>{studioHours}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="mt-10 md:mt-12 pt-6 border-t border-primary-foreground/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="font-mono text-[10px] sm:text-xs opacity-60">
          {copyrightText}
        </p>
        <a 
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors text-sm"
          data-testid="footer-whatsapp-cta"
        >
          <WhatsAppIcon size={16} />
          <span>Chat on WhatsApp</span>
        </a>
      </div>
    </footer>
  );
};

export default Footer;
